import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { AgentActivityLog, AgentActivityLogDocumentType } from '@trnsact/trnsact-shared-types';
import { BtnsTabsContainer } from 'components/shared/TabsContainer/BtnsTabsContainer';
import { GET_AGENT_ACTIVITY } from 'pages/AssistantLog/query';
import {
  AGENT_ACTIVITY_LOG_REVIEW_DISMISS,
  DismissAgentActivityLogReviewResponse,
} from 'pages/LenderApplicationReview/queries';
import { LenderReviewLocationState } from 'pages/LenderApplicationReview/types';
import { SmartIntakeFilters } from './SmartIntakeFilters';
import { SmartIntakeTable } from './SmartIntakeTable';
import { SmartIntakeHistoryTable } from './SmartIntakeHistoryTable';
import { SmartIntakeDetailsDrawer } from './SmartIntakeDetailsDrawer';
import { mapAgentActivityToSmartIntakeHistoryRow, mapAgentActivityToSmartIntakeRow } from './SmartIntakeMapper';
import { QuickDatePreset, SmartIntakeFiltersForm, SmartIntakeHistoryOutcome } from './SmartIntakeTypes';

// Empty-state shell for the Smart Intake tab — parsed submissions list lands here once the
// magic-email/document-upload pipeline is wired up on the backend.
export const SmartIntakeTab = () => {
  const history = useHistory();
  const [activePreset, setActivePreset] = useState<QuickDatePreset | null>('lastWeek');
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [detailsActivityId, setDetailsActivityId] = useState<string | null>(null);

  const { control, setValue } = useForm<SmartIntakeFiltersForm>({
    defaultValues: {
      from: moment().subtract(7, 'days').startOf('day'),
      to: moment().endOf('day'),
      confidenceBand: '',
      uploadedBy: '',
    },
  });
  const [from, to, confidenceBand, uploadedBy] = useWatch({
    control,
    name: ['from', 'to', 'confidenceBand', 'uploadedBy'],
  });

  const { data, loading, refetch } = useQuery<{ getAgentActivity: AgentActivityLog[] | null }>(GET_AGENT_ACTIVITY, {
    skip: !from || !to,
    fetchPolicy: 'no-cache',
    variables: { input: { from: from?.toISOString(), to: to?.toISOString() } },
  });

  const [rejectReview] = useMutation<DismissAgentActivityLogReviewResponse>(AGENT_ACTIVITY_LOG_REVIEW_DISMISS);

  const activitiesById = useMemo(() => {
    const map = new Map<string, AgentActivityLog>();
    (data?.getAgentActivity ?? [])
      .filter(activity => activity.documentType === AgentActivityLogDocumentType.CreditApplication)
      .forEach(activity => map.set(activity.agentActivityLogId, activity));
    return map;
  }, [data]);

  const uploadedByOptions = useMemo(() => {
    const names = new Set<string>();
    activitiesById.forEach(activity => {
      const row = mapAgentActivityToSmartIntakeRow(activity);
      if (row.uploadedBy !== '—') names.add(row.uploadedBy);
    });
    return Array.from(names).sort();
  }, [activitiesById]);

  const buildRows = (activities: AgentActivityLog[]) =>
    activities
      .map(mapAgentActivityToSmartIntakeRow)
      .filter(row => !confidenceBand || row.confidenceBand === confidenceBand)
      .filter(row => !uploadedBy || row.uploadedBy === uploadedBy)
      .sort((a, b) => b.uploadDateSortValue - a.uploadDateSortValue);

  const pendingActivities: AgentActivityLog[] = [];
  const historyActivities: AgentActivityLog[] = [];
  activitiesById.forEach(activity => (activity.review ? historyActivities : pendingActivities).push(activity));

  const pendingRows = buildRows(pendingActivities);
  const historyRows = historyActivities
    .map(mapAgentActivityToSmartIntakeHistoryRow)
    .sort((a, b) => b.uploadDateSortValue - a.uploadDateSortValue);

  const detailsActivity = detailsActivityId ? (activitiesById.get(detailsActivityId) ?? null) : null;
  const detailsOutcome: SmartIntakeHistoryOutcome | null = detailsActivity
    ? (historyRows.find(row => row.id === detailsActivityId)?.outcome ?? null)
    : null;

  const handleReview = (rowId: string) => {
    const activity = activitiesById.get(rowId);
    if (!activity) return;
    // reviewReasons isn't in @trnsact/trnsact-shared-types yet — read it defensively off the raw response.
    const reviewReasons = (activity as unknown as { reviewReasons?: string[] }).reviewReasons ?? [];
    const locationState: LenderReviewLocationState = {
      activity: {
        agentActivityLogId: activity.agentActivityLogId,
        processType: activity.processType,
        documentType: activity.documentType ?? null,
        outcome: activity.outcome ?? null,
        trigger: (activity.trigger as Record<string, unknown>) ?? null,
        payload: (activity.payload as Record<string, unknown>) ?? null,
        associations: (activity.associations as Record<string, unknown>) ?? null,
        reviewReasons,
      },
    };
    history.push(`/lender-review/${activity.agentActivityLogId}`, locationState);
  };

  const handleDismiss = async (rowId: string) => {
    setDismissError(null);
    setDismissingId(rowId);
    try {
      const response = await rejectReview({ variables: { input: { agentActivityLogId: rowId } } });
      if (response.data && !response.data.agentActivityLogReview.dismiss.success) {
        setDismissError(response.data.agentActivityLogReview.dismiss.error ?? 'Could not dismiss this record.');
        return;
      }
      await refetch();
    } catch (err) {
      setDismissError(err instanceof Error ? err.message : 'Could not dismiss this record.');
    } finally {
      setDismissingId(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 3,
          p: 3,
          bgcolor: 'rgba(236, 239, 241, 0.5)',
          border: '1px solid',
          borderColor: 'grey.100',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: '7px',
              bgcolor: 'common.white',
              borderRadius: 1,
              flexShrink: 0,
            }}
          >
            <AutoAwesome sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={500}>
              Automated Application Intake
            </Typography>
            <Typography variant="body1">
              Incoming application emails and uploaded documents are automatically parsed and converted into structured
              submission records. Review extracted data before approving submissions or exporting to your LOS.
            </Typography>
          </Stack>
        </Stack>
        <Button variant="outlined" sx={{ flexShrink: 0 }}>
          Upload document
        </Button>
      </Box>

      {dismissError && (
        <Alert severity="error" onClose={() => setDismissError(null)}>
          {dismissError}
        </Alert>
      )}

      <SmartIntakeFilters
        control={control}
        setValue={setValue}
        activePreset={activePreset}
        onPresetChange={setActivePreset}
        uploadedByOptions={uploadedByOptions}
      />

      <BtnsTabsContainer
        configuration={[
          { key: 'pending', label: 'Pending Review' },
          { key: 'history', label: 'History' },
        ]}
        renderNestedSectionByMode={{
          pending: (
            <SmartIntakeTable
              rows={pendingRows}
              isLoading={loading}
              emptyMessage="No documents have been uploaded or parsed yet."
              onReview={row => handleReview(row.id)}
              onDismiss={row => handleDismiss(row.id)}
              dismissingId={dismissingId}
            />
          ),
          history: (
            <SmartIntakeHistoryTable
              rows={historyRows}
              isLoading={loading}
              emptyMessage="No reviewed submissions yet."
              onViewDetails={row => setDetailsActivityId(row.id)}
            />
          ),
        }}
      />

      <SmartIntakeDetailsDrawer
        activity={detailsActivity}
        outcome={detailsOutcome}
        onClose={() => setDetailsActivityId(null)}
      />

      {pendingRows.length === 0 && historyRows.length === 0 && !loading && (
        <Stack direction="row" spacing={1} sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 2 }}>
          <InfoOutlined sx={{ color: 'primary.main' }} />
          <Typography variant="body1" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
            No documents have been uploaded or parsed yet.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
